import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Bell, BellOff, CheckCheck } from "lucide-react";
import PushToggle from "@/components/app/PushToggle";

const colorFor = (t) => ({
  new_request: "#FFF0D4",
  request_accepted: "#E2F3E4",
  request_rejected: "#FFE4E1",
  achievement: "#EBE4FF",
  activity_completed: "#D4F1F4",
}[t] || "#F4F1EA");

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      if (!e?.__silent401) console.warn("Notifications load failed:", e?.message);
    }
  };
  useEffect(() => { load(); }, []);

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); load(); }
    catch (e) { if (!e?.__silent401) console.warn("read-all failed:", e?.message); }
  };
  const readOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch (e) { if (!e?.__silent401) console.warn("read-one failed:", e?.message); }
  };

  // Render notification title/body via i18n_key when present, else fall back
  // to the server-stored raw title/body. Honours runtime language changes.
  const render = (n) => {
    if (!n.i18n_key) return { title: n.title, body: n.body };
    const params = n.i18n_params || {};
    const titleKey = `notif.keys.${n.i18n_key}.title`;
    const bodyKey = `notif.keys.${n.i18n_key}.body`;
    const tTitle = t(titleKey, { ...params, defaultValue: n.title || "" });
    const tBody = t(bodyKey, { ...params, defaultValue: n.body || "" });
    return { title: tTitle, body: tBody };
  };
  // Re-render on language change (i18n exposes the active language; reading it
  // here keeps eslint happy without an extra effect).
  void i18n.language;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">{t("notif.title")}</h1>
        <button onClick={readAll} className="btn-nb" data-testid="read-all-btn"><CheckCheck className="w-4 h-4" strokeWidth={3}/> {t("notif.markAllRead")}</button>
      </div>
      <div className="mb-5"><PushToggle /></div>
      {items.length === 0 ? (
        <div className="card-nb p-10 text-center bg-[#D4F1F4]" data-testid="notifs-empty">
          <BellOff className="w-12 h-12 mx-auto mb-3"/>
          <h3 className="font-display font-black text-2xl">{t("notif.empty")}</h3>
          <p className="text-[#4A4A4A]">{t("notif.emptyBody")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const r = render(n);
            return (
            <button
              key={n.id}
              onClick={() => readOne(n.id)}
              data-testid={`notif-${n.id}`}
              className={`w-full text-left card-nb p-4 transition-all ${n.read ? "opacity-60" : ""}`}
              style={{ background: colorFor(n.type) }}
            >
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 mt-1 shrink-0" strokeWidth={2.5}/>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold">{r.title}</div>
                  <div className="text-sm text-[#4A4A4A]">{r.body}</div>
                  <div className="text-[10px] text-[#7A7A7A] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                {!n.read && <span className="w-3 h-3 rounded-full bg-[#FF6B6B] border-2 border-[#121212] shrink-0"/>}
              </div>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
